/// #433 — Espelho TS do catálogo de modalidades. Fonte única no front do
/// wizard; espelha o Dart (`tracking_core/sport_catalog.dart`) e o C#
/// (`SailingApp.API/Sports/SportCatalog.cs`). Mudou o conjunto, mude os 3
/// juntos — as travas de divergência (SportCatalogParityTests /
/// `sport_catalog_test.dart`) quebram no backend/mobile; aqui o compilador
/// pega via o union `SportSlug` usado em `Category['modality']`.
///
/// Grupo = propulsão × foil (decisão #147 "foil não mistura" preservada).
/// Extensão #797 (Molokabra): embarcação não mistura com prancha —
/// `sailing_canoe` e `catamaran` são vela sem foil, mas cada um em grupo
/// próprio (a bijeção grupo = propulsão × foil deixa de valer para vela).
///
/// **Idioma:** o `label` dos itens é o rótulo CANÔNICO em português (o mesmo
/// do Dart e do C#) e não muda com o idioma do painel. O que a tela mostra
/// sai de `sportLabel`/`sportPropulsionLabel`/`sportFullLabel`, que leem o
/// dicionário `sportsT` (`i18n/messages/sports.ts`) no idioma corrente.
import { sportsT } from '../i18n/messages/sports';

export const SPORT_PROPULSIONS = [
  { id: 'kite', label: 'Kite' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { id: 'wing', label: 'Wing' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { id: 'sail', label: 'Vela' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { id: 'paddle', label: 'Remo' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
] as const;

export type SportPropulsionId = (typeof SPORT_PROPULSIONS)[number]['id'];

export const SPORT_CATALOG = [
  { slug: 'kitesurf', label: 'Bidirecional', propulsion: 'kite', hasFoil: false, rankingGroup: 'kitesurf' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'kite_wave', label: 'Wave', propulsion: 'kite', hasFoil: false, rankingGroup: 'kitesurf' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'kite_foil', label: 'Hydrofoil', propulsion: 'kite', hasFoil: true, rankingGroup: 'kite_foil' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'wing_foil', label: 'Wing Foil', propulsion: 'wing', hasFoil: true, rankingGroup: 'wing_foil' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'parawing_foil', label: 'Parawing Foil', propulsion: 'wing', hasFoil: true, rankingGroup: 'wing_foil' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'windsurf', label: 'Windsurf', propulsion: 'sail', hasFoil: false, rankingGroup: 'windsurf' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'sailing_canoe', label: 'Canoa à Vela', propulsion: 'sail', hasFoil: false, rankingGroup: 'sailing_canoe' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'catamaran', label: 'Catamarã', propulsion: 'sail', hasFoil: false, rankingGroup: 'catamaran' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'sup', label: 'SUP', propulsion: 'paddle', hasFoil: false, rankingGroup: 'paddle' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'sup_foil', label: 'SUP Foil', propulsion: 'paddle', hasFoil: true, rankingGroup: 'paddle_foil' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'surfski', label: 'Surfski', propulsion: 'paddle', hasFoil: false, rankingGroup: 'paddle' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'outrigger', label: 'Canoa Havaiana', propulsion: 'paddle', hasFoil: false, rankingGroup: 'paddle' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
  { slug: 'paddleboard', label: 'Paddleboard', propulsion: 'paddle', hasFoil: false, rankingGroup: 'paddle' }, // i18n-ignore rótulo canônico, espelho do catálogo do backend/app; tradução em sportsT
] as const satisfies ReadonlyArray<{
  slug: string;
  label: string;
  propulsion: SportPropulsionId;
  hasFoil: boolean;
  rankingGroup: string;
}>;

/// Union dos slugs — `Category['modality']` usa este tipo: esporte novo no
/// catálogo propaga pro wizard sem caçar literais.
export type SportSlug = (typeof SPORT_CATALOG)[number]['slug'];

/// Rótulo da modalidade no idioma do painel ("Hydrofoil", "Twintip").
export function sportLabel(slug: SportSlug): string {
  return sportsT().labels[slug];
}

/// Rótulo da propulsão no idioma do painel ("Vela", "Sail") — o `<optgroup>`
/// dos selects do wizard.
export function sportPropulsionLabel(id: SportPropulsionId): string {
  return sportsT().propulsions[id];
}

/// Rótulo desambiguado ("Kite · Hydrofoil") pros selects/tabelas — Vela fica
/// sem prefixo (rótulos já se explicam: "Windsurf", "Canoa à Vela",
/// "Catamarã"), e label que já contém a propulsão não repete. A regra roda
/// sobre o rótulo JÁ traduzido: em inglês "Paddleboard" contém "Paddle" e
/// fica sem prefixo, em português "Remo · Paddleboard".
export function sportFullLabel(slug: string): string {
  const s = SPORT_CATALOG.find((c) => c.slug === slug);
  if (!s) return slug; // valor legado/desconhecido: mostra cru, não inventa
  const label = sportLabel(s.slug);
  const prop = sportPropulsionLabel(s.propulsion);
  if (s.propulsion === 'sail' || label.includes(prop)) return label;
  return `${prop} · ${label}`;
}

/// Modalidades agrupadas por propulsão, na ordem do catálogo — alimenta os
/// `<optgroup>` dos selects do wizard. O rótulo exibido do grupo vem de
/// `sportPropulsionLabel(g.propulsion.id)`, não do `label` canônico.
export const SPORTS_BY_PROPULSION = SPORT_PROPULSIONS.map((p) => ({
  propulsion: p,
  sports: SPORT_CATALOG.filter((s) => s.propulsion === p.id),
})).filter((g) => g.sports.length > 0);
